import { useState, useEffect } from 'react';
import { exchangeAPI } from '../lib/api';
import { FiMail, FiCheck, FiX, FiLoader, FiTrash2, FiAlertCircle, FiSettings, FiMinusCircle } from 'react-icons/fi';
import ExchangeMailboxBrowser from './ExchangeMailboxBrowser';

export default function ExchangeSettings() {
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [showBrowser, setShowBrowser] = useState(false);

  useEffect(() => {
    loadConnections();
  }, []);

  const loadConnections = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await exchangeAPI.getConnections();
      setConnections(response.data.connections || []);
    } catch (err) {
      console.error('Failed to load Exchange connections:', err);
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    try {
      setConnecting(true);
      setError(null);
      await exchangeAPI.connect();
      setSuccess('Exchange tenant connected successfully!');
      setTimeout(() => setSuccess(null), 3000);
      await loadConnections();
    } catch (err) {
      console.error('Failed to connect Exchange:', err);
      setError(err.response?.data?.error || err.message);
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async (connectionId) => {
    if (!confirm('Are you sure you want to disconnect this Exchange account?')) {
      return;
    }

    try {
      setError(null);
      await exchangeAPI.disconnect(connectionId);
      setSuccess('Exchange account disconnected successfully!');
      setTimeout(() => setSuccess(null), 3000);
      loadConnections();
    } catch (err) {
      console.error('Failed to disconnect:', err);
      setError(err.response?.data?.error || err.message);
    }
  };

  const handleManageConnection = (connection) => {
    setSelectedConnection(connection);
    setShowBrowser(true);
  };

  const handleBrowserComplete = () => {
    setShowBrowser(false);
    setSelectedConnection(null);
    setSuccess('Mailbox selection saved successfully!');
    setTimeout(() => setSuccess(null), 3000);
    loadConnections();
  };

  const handleRemoveAllMailboxes = async (connection) => {
    if (!confirm('Remove all connected mailboxes from this connection?')) return;
    try {
      setError(null);
      await exchangeAPI.saveMailboxes(connection.id, []);
      setSuccess('All mailboxes removed.');
      setTimeout(() => setSuccess(null), 3000);
      loadConnections();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const handleRemoveMailbox = async (connection, userId) => {
    const updated = (connection.connected_mailboxes || []).filter(m => m.user_id !== userId);
    try {
      setError(null);
      await exchangeAPI.saveMailboxes(connection.id, updated);
      setSuccess('Mailbox removed.');
      setTimeout(() => setSuccess(null), 3000);
      loadConnections();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-50';
      case 'expired': return 'text-yellow-600 bg-yellow-50';
      case 'disconnected': return 'text-gray-600 bg-gray-50';
      case 'error': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return <FiCheck className="w-4 h-4" />;
      case 'expired':
      case 'error': return <FiAlertCircle className="w-4 h-4" />;
      default: return <FiX className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <FiLoader className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (showBrowser && selectedConnection) {
    return (
      <ExchangeMailboxBrowser
        connection={selectedConnection}
        onComplete={handleBrowserComplete}
        onCancel={() => {
          setShowBrowser(false);
          setSelectedConnection(null);
        }}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <FiMail className="w-6 h-6 text-purple-600" />
            Exchange Email Search
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Connect Exchange mailboxes to search emails from Doxolve and the AI Assistant
          </p>
        </div>
        <button
          onClick={handleConnect}
          disabled={connecting}
          className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {connecting ? (
            <>
              <FiLoader className="w-4 h-4 mr-2 animate-spin" />
              Connecting...
            </>
          ) : (
            <>
              <FiMail className="w-4 h-4 mr-2" />
              Connect Exchange
            </>
          )}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
          <FiAlertCircle className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-medium text-red-800">Error</h3>
            <p className="text-sm text-red-700 mt-1">{error}</p>
          </div>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start">
          <FiCheck className="w-5 h-5 text-green-600 mt-0.5 mr-3 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-medium text-green-800">Success</h3>
            <p className="text-sm text-green-700 mt-1">{success}</p>
          </div>
        </div>
      )}

      {connections.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <FiMail className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Exchange Connections</h3>
          <p className="text-gray-600 mb-4">
            Connect your Exchange tenant to search emails directly from Doxolve
          </p>
          <button
            onClick={handleConnect}
            disabled={connecting}
            className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
          >
            <FiMail className="w-4 h-4 mr-2" />
            Connect Now
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {connections.map((connection) => (
            <div
              key={connection.id}
              className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <FiMail className="w-8 h-8 text-purple-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-medium text-gray-900">
                        {connection.tenant_name || 'Exchange Account'}
                      </h3>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(connection.status)}`}>
                        {getStatusIcon(connection.status)}
                        <span className="ml-1 capitalize">{connection.status}</span>
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      Tenant ID: {connection.tenant_id}
                    </p>
                    {connection.connected_mailboxes && connection.connected_mailboxes.length > 0 && (
                      <div className="mt-2">
                        <p className="text-sm font-medium text-gray-700 mb-1">
                          Connected Mailboxes ({connection.connected_mailboxes.length}):
                        </p>
                        <ul className="space-y-0.5">
                          {connection.connected_mailboxes.map((mb, idx) => (
                            <li key={idx} className="flex items-center justify-between group text-sm text-gray-600 pr-1">
                              <span className="flex items-center min-w-0">
                                <FiMail className="w-3 h-3 mr-1.5 flex-shrink-0 text-purple-400" />
                                <span className="truncate">
                                  {mb.display_name} <span className="text-gray-400">({mb.email})</span>
                                </span>
                              </span>
                              <button
                                onClick={() => handleRemoveMailbox(connection, mb.user_id)}
                                className="opacity-0 group-hover:opacity-100 ml-2 flex-shrink-0 text-red-400 hover:text-red-600 transition-opacity"
                                title="Remove mailbox"
                              >
                                <FiMinusCircle className="w-3.5 h-3.5" />
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleManageConnection(connection)}
                    className="inline-flex items-center px-3 py-1.5 text-sm bg-purple-50 text-purple-700 rounded hover:bg-purple-100 transition-colors"
                  >
                    <FiSettings className="w-4 h-4 mr-1" />
                    Manage
                  </button>
                  {connection.connected_mailboxes && connection.connected_mailboxes.length > 0 && (
                    <button
                      onClick={() => handleRemoveAllMailboxes(connection)}
                      className="inline-flex items-center px-3 py-1.5 text-sm bg-orange-50 text-orange-700 rounded hover:bg-orange-100 transition-colors"
                      title="Remove all connected mailboxes"
                    >
                      <FiX className="w-4 h-4 mr-1" />
                      Remove All
                    </button>
                  )}
                  <button
                    onClick={() => handleDisconnect(connection.id)}
                    className="inline-flex items-center px-3 py-1.5 text-sm bg-red-50 text-red-700 rounded hover:bg-red-100 transition-colors"
                  >
                    <FiTrash2 className="w-4 h-4 mr-1" />
                    Disconnect
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-purple-900 mb-2">About Exchange Integration</h4>
        <ul className="text-sm text-purple-800 space-y-1">
          <li>• Emails are searched in real-time - nothing is stored locally</li>
          <li>• Search emails directly from the Search page alongside documents</li>
          <li>• AI Assistant can reference emails when answering questions</li>
          <li>• Download email attachments directly from search results</li>
          <li>• Only selected mailboxes are searched (admin-controlled)</li>
          <li>• Requires Mail.Read (Application) permission in Azure AD</li>
        </ul>
      </div>
    </div>
  );
}
